import { SERIALIZATION_PRECISION } from '@geom';
import { SVGImageSource } from '@image';
import { Token } from '@parser';
import { IString } from '@strings';
import { assertInstanceOf, expectDefined, fixedNumber } from '@utils';
import type { CadetDoc } from './CadetDoc';
import {
  type ElementDirective,
  AssistantNoteDirective,
  BoundsDirective,
  ClassDirective,
  EdgeClassDirective,
  GroupClassDirective,
  ImageDirective,
  IncludeDirective,
  NoteDirective,
  PathDirective,
  SettingsDirective,
  StateDirective,
  UnitClassDirective
} from './Directives';
import { compareIdentifiersNatural, newIdentifier } from './Identifier';
import { Prologue } from './Prologue';
import { EdgeStatement, GroupStatement, Statement, UnitStatement } from './Statements';
import { Unparser } from './Unparser';
import { ObjectValue } from './Values';

/* ------------------------------------------------------------- */
/*  Helpers for “layoutAtEnd”                                    */
/* ------------------------------------------------------------- */

// When layoutAtEnd == true we collect these two kinds of directives
// while we are gathering per-element directives, then emit them in
// sorted blocks at the end of the document.
interface LayoutBuckets {
  bounds: BoundsDirective[];
  paths: PathDirective[];
}

export class CadetWriter {
  #unparser: Unparser;
  private readonly _layoutAtEnd: boolean;
  private readonly _layoutBuckets?: LayoutBuckets;

  constructor(doc: CadetDoc, opts?: { layoutAtEnd?: boolean }) {
    this._layoutAtEnd = opts?.layoutAtEnd ?? false;
    this._layoutBuckets = this._layoutAtEnd ? { bounds: [], paths: [] } : undefined;
    const indent = '  ';
    // NOTE: Statement reconstruction happens first, but writing order is controlled below.
    const statements = this.createGroupHierarchy(doc);

    this.#unparser = new Unparser(indent);
    const u = this.#unparser;

    // 1. Write Prologue
    const prologue = Prologue.currentPrologue;
    prologue.unparse(u);
    u.addLeft(';', '');
    u.endLine();
    u.endLine(); // Add semicolon and blank line after prologue

    // 2. Write declared :INCLUDE directives (in order), then a blank line
    if (doc.includes.length > 0) {
      doc.includes.forEach(symbol => {
        const symToken = new Token<string>(symbol, { value: symbol });
        const include = new IncludeDirective(symToken);
        include.unparse(u);
        u.addLeft(';', '');
        u.endLine();
      });
      // One blank line after includes block
      u.endLine();
    }

    // <<< START: Add :SETTINGS Serialization >>>
    // 3. Write :SETTINGS Directive (if present and not empty)
    if (doc.rawSettings && Object.keys(doc.rawSettings).length > 0) {
      // Create an ObjectValue from the raw JS object stored in CadetDoc
      const settingsObjectValue = new ObjectValue(doc.rawSettings);
      // Create a SettingsDirective instance (start position isn't needed for writing)
      const settingsDirective = new SettingsDirective(settingsObjectValue);
      // Unparse the directive
      settingsDirective.unparse(u);
      // Add semicolon and blank line after :SETTINGS
      u.addLeft(';', '');
      u.endLine();
      u.endLine();
    }
    // <<< END: Add :SETTINGS Serialization >>>

    // 4. Write Class Directives
    const classDirectives = this.createClassDirectives(doc);
    if (classDirectives.length > 0) {
      this.writeClassDirectives(classDirectives, u);
      // Add blank line *after* all class directives if any were written
      u.endLine();
    }

    // 5. Write Statements
    this.writeStatements(statements, u);

    // 5.5 Write Image Directives (deterministic order, before layout buckets)
    this.writeImageDirectives(doc, u);

    // 6. If requested, append layout directives at end
    this.writeLayoutBuckets(u);
  }

  /* ------------------------------------------------------------------ */
  /*  Directive collection helpers                                       */
  /* ------------------------------------------------------------------ */

  private gatherElementDirectives(
    doc: CadetDoc,
    elementId: string,
    buckets?: LayoutBuckets
  ): ElementDirective[] {
    const directives: ElementDirective[] = [];
    const graph = doc.graph;
    const isNode = graph.hasNode(elementId);
    const isEdge = !isNode && graph.hasEdge(elementId); // Assumes ID is either node or edge if present

    // <<< CHANGE: Create Token<string> (StringToken) with value >>>
    // Create token for directive constructors, providing the string value
    const elementIdToken = new Token<string>(elementId, { value: elementId });

    // --- Gather existing directives (Notes, Layout) ---
    // These constructors expect StringToken for the id parameter

    if (isNode) {
      // Notes for Nodes
      if (doc.hasNodeNote(elementId)) {
        const text = doc.nodeNote(elementId).toString();
        directives.push(new NoteDirective(elementIdToken, new Token<string>(text)));
      }
      if (doc.hasNodeAssistantNote(elementId)) {
        const text = doc.nodeAssistantNote(elementId).toString();
        directives.push(
          new AssistantNoteDirective(elementIdToken, new Token<string>(text))
        );
      }
      // Bounds for Nodes
      const nodeLayout = doc.layout.node(elementId);
      if (nodeLayout?.bounds) {
        const { x, y, width, height } = nodeLayout.bounds.ltwh;
        const f = (n: number) => fixedNumber(n, SERIALIZATION_PRECISION);
        const boundsStr = `${f(x)} ${f(y)} ${f(width)} ${f(height)}`;
        const dir = new BoundsDirective(
          elementIdToken,
          new Token<string>(JSON.stringify(boundsStr), { value: boundsStr })
        );
        if (buckets) buckets.bounds.push(dir);
        else directives.push(dir);
      }
    } else if (isEdge) {
      // Notes for Edges
      if (doc.hasEdgeNote(elementId)) {
        const text = doc.edgeNote(elementId).toString();
        directives.push(new NoteDirective(elementIdToken, new Token<string>(text)));
      }
      if (doc.hasEdgeAssistantNote(elementId)) {
        const text = doc.edgeAssistantNote(elementId).toString();
        directives.push(
          new AssistantNoteDirective(elementIdToken, new Token<string>(text))
        );
      }
      // Path for Edges
      const edgeLayout = doc.layout.edge(elementId);
      if (edgeLayout?.path) {
        const pathData = edgeLayout.path.toSvgPathData(SERIALIZATION_PRECISION);
        const dir = new PathDirective(
          elementIdToken,
          new Token<string>(JSON.stringify(pathData), { value: pathData })
        );
        if (buckets) buckets.paths.push(dir);
        else directives.push(dir);
      }
    }

    // --- START: Gather non-reserved attributes for :STATE ---
    const reservedKeys = ['title', 'note', 'assistantNote']; // Keys managed by other syntax/directives
    const instanceAttrs = isNode
      ? doc.nodeData(elementId).attrs
      : isEdge
        ? doc.edgeData(elementId).attrs
        : {};
    const stateAttrs: Record<string, unknown> = {};

    // Sort attribute keys deterministically for consistent output
    const sortedAttrEntries = Object.entries(instanceAttrs).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    for (const [key, value] of sortedAttrEntries) {
      if (!reservedKeys.includes(key)) {
        stateAttrs[key] = value;
      }
    }

    // If non-reserved attributes exist, create a :STATE directive
    if (Object.keys(stateAttrs).length > 0) {
      const stateObjectValue = new ObjectValue(stateAttrs);
      // StateDirective constructor expects StringToken for id
      directives.push(new StateDirective(elementIdToken, stateObjectValue));
    }
    // --- END: Gather non-reserved attributes for :STATE ---

    // Assuming Statement unparse handles sorting directives alphabetically later.
    // If not, sort here: directives.sort((a, b) => a.name.localeCompare(b.name));

    return directives;
  }

  private createGroupHierarchy(doc: CadetDoc): Statement[] {
    const graph = doc.graph;
    const forest = graph.forest;
    const edgePlacementMap = new Map<string | undefined, EdgeStatement[]>();
    const visitedNodes = new Set<string>();

    const doesEdgeNeedId = (edgeId: string): boolean => {
      const hasLayoutDirective = doc.layout.edges.has(edgeId);
      const hasNotes = doc.hasEdgeNote(edgeId) || doc.hasEdgeAssistantNote(edgeId);
      if (hasLayoutDirective || hasNotes) return true;

      // Check if edge has attributes (from :STATE directives or other references)
      // Ignore empty note and assistantNote attributes as they're automatically added
      const edgeAttrs = doc.edgeData(edgeId).attrs;
      const filteredAttrs = Object.fromEntries(
        Object.entries(edgeAttrs).filter(([key, value]) => {
          // Skip empty note attributes (IString objects with isEmpty = true)
          if (
            (key === 'note' || key === 'assistantNote') &&
            value instanceof IString &&
            value.isEmpty
          ) {
            return false;
          }
          return true;
        })
      );
      const hasAttributes = Object.keys(filteredAttrs).length > 0;
      if (hasAttributes) return true;

      const sourceId = graph.source(edgeId);
      const targetId = graph.target(edgeId);
      // Check if there are multiple edges between the same nodes (regardless of class)
      // This requires ID disambiguation
      for (const otherEdgeId of graph.edges) {
        if (otherEdgeId === edgeId) continue;
        if (
          graph.source(otherEdgeId) === sourceId &&
          graph.target(otherEdgeId) === targetId
        ) {
          return true; // Multiple edges between same nodes need ID disambiguation
        }
      }
      // Edge ID is only needed if it's functionally referenced elsewhere in the document
      // (layout directives, notes, or for disambiguation), not based on syntax patterns
      return false;
    };

    // Sort edges deterministically for consistent output
    const sortedEdges = Array.from(graph.edges).sort(compareIdentifiersNatural);
    for (const edgeId of sortedEdges) {
      const sourceId = graph.source(edgeId);
      const targetId = graph.target(edgeId);
      const parentS = forest.parent(sourceId);
      const parentT = forest.parent(targetId);
      let lcaGroupId: string | undefined;
      if (parentS === parentT) {
        lcaGroupId = parentS;
      } else if (parentS === undefined || parentT === undefined) {
        lcaGroupId = undefined;
      } else {
        lcaGroupId = forest.leastCommonAncestor(new Set([parentS, parentT])) ?? undefined;
      }

      const edgeClsStr = doc.edgeClass(edgeId);
      const edgeToken = new Token<string>(edgeClsStr, { value: edgeClsStr });
      const sourceStmt = new UnitStatement(sourceId);
      const targetStmt = new UnitStatement(targetId);

      let finalEdgeId: string | undefined;
      if (doesEdgeNeedId(edgeId)) {
        const defaultIdPattern = sourceId + edgeClsStr + targetId;
        finalEdgeId = edgeId === defaultIdPattern ? newIdentifier() : edgeId;
      }

      const edgeDirectives = this.gatherElementDirectives(
        doc,
        edgeId,
        this._layoutBuckets
      );
      const edgeStatement = new EdgeStatement(sourceStmt, targetStmt, edgeToken, {
        id: finalEdgeId,
        directives: edgeDirectives
      });

      if (!edgePlacementMap.has(lcaGroupId)) {
        edgePlacementMap.set(lcaGroupId, []);
      }
      edgePlacementMap.get(lcaGroupId)?.push(edgeStatement);
    }

    const topLevelStatements: Statement[] = [];
    // Sort root nodes deterministically for consistent output
    const sortedRootNodeIds = Array.from(forest.roots).sort(compareIdentifiersNatural);

    for (const nodeId of sortedRootNodeIds) {
      if (doc.isGroup(nodeId)) {
        topLevelStatements.push(
          this.buildGroupStatementRecursive(doc, nodeId, edgePlacementMap, visitedNodes)
        );
        visitedNodes.add(nodeId);
      }
    }

    for (const nodeId of sortedRootNodeIds) {
      if (doc.isUnit(nodeId) && !visitedNodes.has(nodeId)) {
        const nodeClassName = doc.nodeClass(nodeId);
        const nodeTitleRaw = doc.nodeTitle(nodeId);
        assertInstanceOf(nodeTitleRaw, IString);
        const titleToken = nodeTitleRaw.isEmpty
          ? undefined
          : new Token<string>(nodeTitleRaw.toString());
        const clsToken =
          nodeClassName === 'Unit'
            ? undefined
            : new Token(`/${nodeClassName}`, { value: nodeClassName });
        // titleToken already computed
        const nodeDirectives = this.gatherElementDirectives(
          doc,
          nodeId,
          this._layoutBuckets
        );
        topLevelStatements.push(
          new UnitStatement(nodeId, {
            title: titleToken,
            cls: clsToken,
            directives: nodeDirectives
          })
        );
        visitedNodes.add(nodeId);
      }
    }

    const rootEdges = edgePlacementMap.get(undefined) ?? [];
    topLevelStatements.push(...rootEdges);

    // Sort unvisited nodes deterministically for consistent output
    const sortedAllNodes = Array.from(doc.graph.nodes).sort(compareIdentifiersNatural);
    for (const nodeId of sortedAllNodes) {
      if (!visitedNodes.has(nodeId)) {
        console.warn(`CadetWriter: Node ${nodeId} unvisited. Adding as standalone.`);
        const nodeDirectives = this.gatherElementDirectives(
          doc,
          nodeId,
          this._layoutBuckets
        );
        topLevelStatements.push(
          new UnitStatement(nodeId, { directives: nodeDirectives })
        );
      }
    }
    return topLevelStatements;
  }

  private buildGroupStatementRecursive(
    doc: CadetDoc,
    groupId: string,
    edgePlacementMap: Map<string | undefined, EdgeStatement[]>,
    visitedNodes: Set<string>
  ): GroupStatement {
    const forest = doc.graph.forest;
    const childUnitStatements: UnitStatement[] = [];
    const childGroupStatements: GroupStatement[] = [];
    const edgesForThisGroup: EdgeStatement[] = [];

    // Sort child nodes deterministically for consistent output
    const sortedChildNodeIds = Array.from(forest.children(groupId)).sort(
      compareIdentifiersNatural
    );
    for (const childId of sortedChildNodeIds) {
      if (!visitedNodes.has(childId)) {
        if (doc.isUnit(childId)) {
          const nodeClassName = doc.nodeClass(childId);
          const nodeTitleRaw = doc.nodeTitle(childId);
          assertInstanceOf(nodeTitleRaw, IString);
          const clsToken =
            nodeClassName === 'Unit'
              ? undefined
              : new Token(`/${nodeClassName}`, { value: nodeClassName });
          const titleToken = nodeTitleRaw.isEmpty
            ? undefined
            : new Token<string>(nodeTitleRaw.toString());
          const childDirectives = this.gatherElementDirectives(
            doc,
            childId,
            this._layoutBuckets
          );
          childUnitStatements.push(
            new UnitStatement(childId, {
              title: titleToken,
              cls: clsToken,
              directives: childDirectives
            })
          );
          visitedNodes.add(childId);
        } else if (doc.isGroup(childId)) {
          childGroupStatements.push(
            this.buildGroupStatementRecursive(
              doc,
              childId,
              edgePlacementMap,
              visitedNodes
            )
          );
          visitedNodes.add(childId);
        }
      }
    }

    const edgesMappedToGroup = edgePlacementMap.get(groupId) ?? [];
    edgesForThisGroup.push(...edgesMappedToGroup);
    const groupItems: Statement[] = [
      ...childUnitStatements,
      ...childGroupStatements,
      ...edgesForThisGroup
    ];

    const groupClassName = doc.nodeClass(groupId);
    const groupTitleRaw = doc.nodeTitle(groupId);
    assertInstanceOf(groupTitleRaw, IString);
    const cls =
      groupClassName === 'Group'
        ? undefined
        : new Token(`/${groupClassName}`, { value: groupClassName });
    const title = groupTitleRaw.isEmpty
      ? undefined
      : new Token<string>(groupTitleRaw.toString());

    let isReferenced = false;
    // Check edge references (existing logic)
    for (const edgeId of [
      ...doc.graph.inEdges(groupId),
      ...doc.graph.outEdges(groupId)
    ]) {
      if (!forest.hasEdge(edgeId)) {
        // Ignore containment edges
        isReferenced = true;
        break;
      }
    }
    // ALSO Check if referenced by directives (NEW logic)
    if (!isReferenced) {
      // Only check if not already referenced by an edge
      // Check if the doc has *any* directive data associated with this group ID
      if (
        doc.hasNodeNote(groupId) ||
        doc.hasNodeAssistantNote(groupId) ||
        doc.layout.node(groupId) // Check if layout bounds exist
      ) {
        isReferenced = true;
      }
      // Note: We don't check for Path directives as groups don't have paths.
    }
    const finalGroupId = isReferenced ? groupId : undefined;

    const groupDirectives = this.gatherElementDirectives(
      doc,
      groupId,
      this._layoutBuckets
    );
    return new GroupStatement(groupItems, {
      id: finalGroupId,
      title,
      cls,
      directives: groupDirectives
    });
  }

  /* ------------------------------------------------------------- */
  /*  Layout-at-end output                                         */
  /* ------------------------------------------------------------- */

  private writeLayoutBuckets(u: Unparser): void {
    if (!this._layoutAtEnd || !this._layoutBuckets) return;
    const { bounds, paths } = this._layoutBuckets;

    const sortById = (a: ElementDirective, b: ElementDirective) =>
      compareIdentifiersNatural(a.id.value, b.id.value);

    if (bounds.length > 0) {
      u.endLine();
      u.endLine();
      bounds.sort(sortById);
      bounds.forEach((d, i) => {
        d.unparse(u);
        if (i < bounds.length - 1) {
          u.addLeft(';', '');
          u.endLine();
        }
      });
    }

    if (paths.length > 0) {
      u.endLine();
      u.endLine();
      paths.sort(sortById);
      paths.forEach((d, i) => {
        d.unparse(u);
        if (i < paths.length - 1) {
          u.addLeft(';', '');
          u.endLine();
        }
      });
    }
  }

  private createClassDirectives(doc: CadetDoc): ClassDirective[] {
    const directives: ClassDirective[] = [];
    const processedClasses = new Set<string>([
      'Element',
      'Node',
      'Edge',
      'Unit',
      'Group'
    ]);
    const allClassesInDoc = Array.from(doc.classes.tree.nodes);
    // If includes are declared, skip any class that originated from an include
    const classesToProcess = allClassesInDoc.filter(cls => {
      if (processedClasses.has(cls)) return false;
      if (doc.includes.length > 0 && doc.includeClassOrigins.has(cls)) return false;
      return true;
    });

    const getDirectiveType = (className: string): ':UNIT' | ':GROUP' | ':EDGE' => {
      const isClassKindOf = doc.isClassKindOf.bind(doc);
      if (isClassKindOf(className, 'Unit')) return ':UNIT';
      if (isClassKindOf(className, 'Group')) return ':GROUP';
      if (isClassKindOf(className, 'Edge')) return ':EDGE';
      console.warn(`CadetWriter: Class ${className} unknown kind. Defaulting :UNIT.`);
      return ':UNIT';
    };

    const inheritanceMap = new Map<string, string>();
    // Fix L240: Ensure map population loop is here and remove trailing {}
    for (const className of allClassesInDoc) {
      const inEdges = doc.classes.tree.inEdges(className);
      for (const edge of inEdges) {
        const parentName = doc.classes.tree.source(edge);
        if (parentName) {
          inheritanceMap.set(className, parentName);
          break;
        }
      }
    } // Removed stray {}

    for (const className of classesToProcess) {
      const superClassName = inheritanceMap.get(className);
      let implicitDefaultSuperClass: string;
      const directiveTypeStr = getDirectiveType(className);
      if (directiveTypeStr === ':UNIT') implicitDefaultSuperClass = 'Unit';
      else if (directiveTypeStr === ':GROUP') implicitDefaultSuperClass = 'Group';
      else implicitDefaultSuperClass = 'Edge';
      const shouldIncludeSuperClass = !!(
        superClassName && superClassName !== implicitDefaultSuperClass
      );

      const classIsArrow = className.startsWith('-') || className.startsWith('=');
      const classDisplay = classIsArrow ? className : `/${className}`;
      const classToken = new Token<string>(classDisplay, { value: className });

      let superClassToken: Token<string> | undefined;
      if (shouldIncludeSuperClass && superClassName) {
        const superClassIsArrow =
          superClassName.startsWith('-') || superClassName.startsWith('=');
        const superClassDisplay = superClassIsArrow
          ? superClassName
          : `/${superClassName}`;
        superClassToken = new Token<string>(superClassDisplay, {
          value: superClassName
        });
      }

      const attrs = doc.classes.tree.nodeData(className).attrs;
      const objectValue = new ObjectValue(attrs);
      let directive: ClassDirective;
      if (directiveTypeStr === ':UNIT') {
        directive = new UnitClassDirective(classToken, objectValue, {
          superCls: superClassToken
        });
      } else if (directiveTypeStr === ':GROUP') {
        directive = new GroupClassDirective(classToken, objectValue, {
          superCls: superClassToken
        });
      } else {
        directive = new EdgeClassDirective(classToken, objectValue, {
          superCls: superClassToken
        });
      }
      directives.push(directive);
    }
    // --- Inheritance-aware ordering -------------------------------------------
    type DirectiveTypeName = ':UNIT' | ':GROUP' | ':EDGE';
    const typeOrder: Record<DirectiveTypeName, number> = {
      ':UNIT': 1,
      ':GROUP': 2,
      ':EDGE': 3
    };

    // Cache depths so we only compute each class once
    const depthCache = new Map<string, number>();

    const classDepth = (className: string): number => {
      if (depthCache.has(className)) return expectDefined(depthCache.get(className));
      let d = 0;
      try {
        let current = className;
        for (;;) {
          const parents = doc.classes.tree.inEdges(current);
          if (parents.size === 0) break;
          const parent = doc.classes.tree.source([...parents][0]);
          if (!parent) break;
          d += 1;
          current = parent;
        }
      } catch {
        /* root reached */
      }
      depthCache.set(className, d);
      return d;
    };

    directives.sort((a, b) => {
      const typeComp =
        typeOrder[a.name as DirectiveTypeName] - typeOrder[b.name as DirectiveTypeName];
      if (typeComp !== 0) return typeComp;

      // Same directive type → order by depth (superclasses first)
      const da = classDepth(a.cls.value);
      const db = classDepth(b.cls.value);
      if (da !== db) return da - db;

      // Tie-breaker: lexicographic class name
      return a.cls.value.localeCompare(b.cls.value);
    });
    // ---------------------------------------------------------------------------
    return directives;
  }

  private writeClassDirectives(directives: ClassDirective[], u: Unparser): void {
    for (let i = 0; i < directives.length; i++) {
      directives[i].unparse(u);
      u.endLine();
      if (i < directives.length - 1) {
        u.endLine();
      }
    }
  }

  private writeStatements(statements: Statement[], u: Unparser): void {
    const unitStatements = statements.filter(
      (s): s is UnitStatement => s instanceof UnitStatement
    );
    const edgeStatements = statements.filter(
      (s): s is EdgeStatement => s instanceof EdgeStatement
    );
    const groupStatements = statements.filter(
      (s): s is GroupStatement => s instanceof GroupStatement
    );
    // Keep the sorted order: Units, Edges, Groups
    const sortedStatements = [...unitStatements, ...edgeStatements, ...groupStatements];

    sortedStatements.forEach((statement, i) => {
      const hasNext = i < sortedStatements.length - 1;
      const nextStatement = hasNext ? sortedStatements[i + 1] : undefined;

      statement.unparse(u);

      if (hasNext && !u.atLineStart) {
        if (statement instanceof GroupStatement) {
          // groups are closed with ‘}’ – only need a newline for pretty
          u.endLine(); // pretty → newline, compact → no output but keeps spacing
        } else {
          // Unit / Edge need the real delimiter
          u.addLeft(';', ''); // compact
          u.endLine(); // pretty
        }
      }

      // add extra blank line between logical blocks (units→non-unit, edges→non-edge)
      if (
        hasNext &&
        ((statement instanceof UnitStatement &&
          !(nextStatement instanceof UnitStatement)) ||
          (statement instanceof EdgeStatement &&
            !(nextStatement instanceof EdgeStatement)))
      ) {
        u.endLine(); // extra blank line
      }
    });
  }

  /* ------------------------------------------------------------- */
  /*  Image directives output                                       */
  /* ------------------------------------------------------------- */

  private writeImageDirectives(doc: CadetDoc, u: Unparser): void {
    const images = doc.images;
    if (images.size === 0) return;

    const entries = Array.from(images.entries());
    // Deterministic ordering by imageID (natural compare to match identifiers)
    entries.sort((a, b) => compareIdentifiersNatural(a[0], b[0]));

    // No explicit padding here: the prologue and statements already manage spacing.

    entries.forEach(([, src], i) => {
      // Determine value string using precedence rules:
      // For this phase, prefer exact origin string if present;
      // otherwise, fall back to SVG content when available.
      let imageValue = '';
      if (src.origin) {
        imageValue = src.origin.value;
      } else if (src instanceof SVGImageSource) {
        // Preserve the raw SVG text when no explicit origin exists
        imageValue = src.svgContent;
      } else {
        // As a last resort, use a descriptive placeholder
        imageValue = src.toString();
      }

      // Construct tokens
      const idToken = new Token<string>(expectDefined(src.id), {
        value: src.id
      });
      const valueToken = new Token<string>(JSON.stringify(imageValue), {
        value: imageValue
      });
      const dir = new ImageDirective(idToken, valueToken);
      dir.unparse(u);
      if (i < entries.length - 1) {
        u.addLeft(';', '');
        u.endLine();
      }
    });
  }

  get compact(): string {
    return this.#unparser.compact();
  }
  get pretty(): string {
    return this.#unparser.pretty();
  }
}
